import useSWR, { KeyedMutator, type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import type {
  DomainInvalidReason,
  NetworkType,
  OrgOpSignableDecision,
  ProposalValidity,
} from '@mls-wallet/shared-types';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

// UI-specific type that matches what the API returns for the proposal object
export type AccountProposal = {
  id: string;
  name: string;
  networkIds: NetworkType[];
  multichainAccountId: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  validity: ProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  createdAt: Date;
};

// Signature data from the API
type ProposalSignature = {
  proposalType: string;
  proposalId: string;
  reviewingUserId: string;
  decision: OrgOpSignableDecision;
  signature: string | null;
  createdAt: Date;
  updatedAt: Date;
};

// Enriched proposal with signature data (matches backend ProposalWithSignatures)
export type ProposalWithSignatures = {
  proposal: AccountProposal;
  isExecuting: boolean;
  approvals: ProposalSignature[];
  rejections: ProposalSignature[];
  requiredApprovals: number;
  totalVoters: number;
};

export type AccountProposalsData = {
  proposals: ProposalWithSignatures[];
};

interface UseAccountProposalsReturn {
  proposals: ProposalWithSignatures[];
  isLoading: boolean;
  error: any;
  refresh: KeyedMutator<AccountProposalsData>;
}

const fetcher = async (url: string): Promise<AccountProposalsData> =>
  await denApi.get<AccountProposalsData, void>(url);

/**
 * Hook for fetching account proposals
 * @param swrOptions - optional SWR configuration
 */
export function useAccountProposals(
  swrOptions?: SWRConfiguration<AccountProposalsData>
): UseAccountProposalsReturn {
  const url = 'multichain-account/proposals';

  const { data, error, isLoading, mutate } = useSWR<AccountProposalsData>(
    url,
    fetcher,
    {
      revalidateOnFocus: false,
      refreshInterval: SIGNABLE_POLL_INTERVAL_MS,
      ...(swrOptions ?? {}),
    }
  );

  // When fetching all proposals, return the structured data
  const proposalsData = data as AccountProposalsData;
  return {
    proposals: proposalsData?.proposals ?? [],
    isLoading,
    error,
    refresh: mutate,
  };
}
